import { AppInstanceShort } from '@wpp-open/core'
import { useCallback, useEffect, useState } from 'react'
import { DropResult } from 'react-beautiful-dnd'

import { useAppInstanceTemplatesApi } from 'api/apps/queries/useAppInstanceTemplatesApi'
import { GROUP_DROPPABLE_TYPE } from 'constants/droppableTypes'
import { PageSize } from 'constants/pageSize'
import { useStableCallback } from 'hooks/useStableCallback'
import { useUpdateAppsPosition } from 'hooks/useUpdateAppsPosition'
import { useUpdateGroupPosition } from 'hooks/useUpdateGroupPosition'
import { useUpdateSubGroupPosition } from 'hooks/useUpdateSubGroupPosition'
import { useCurrentTenantData } from 'providers/currentTenantData/CurrentTenantDataContext'
import { TemplateResponse } from 'types/templates/templates'
import { getOrderedEntities, Group, GroupedItem, SubGroupItem } from 'utils/appsGroups'

export const useReorderAppInstancesShort = (appInstances: AppInstanceShort[]) => {
  const [ordered, setOrdered] = useState<AppInstanceShort[]>(() => appInstances)
  const { updateAppsPosition } = useUpdateAppsPosition()
  const {
    navigationTree: { rootId },
  } = useCurrentTenantData()

  useEffect(() => {
    setOrdered(appInstances)
  }, [appInstances])

  const onDragEnd = useStableCallback((result: DropResult) => {
    const { source, destination } = result

    // dropped outside
    if (!destination || source.droppableId !== destination.droppableId) {
      return
    }

    const tempAppInstances = appInstances

    const [removed] = tempAppInstances.splice(source.index, 1)
    tempAppInstances.splice(destination.index, 0, removed)

    const reorderedApps = tempAppInstances.map((app, index) => ({
      ...app,
      position: index + 1,
    }))

    // calls the api only with the apps that updated their position
    const startInterval = Math.min(source.index, destination.index)
    const endInterval = Math.max(source.index, destination.index)
    const onlyChangeApps = reorderedApps.slice(startInterval, endInterval + 1)

    const payload = onlyChangeApps.map(app => ({
      id: app.id,
      position: app.position,
    }))

    setOrdered(reorderedApps)
    updateAppsPosition({ data: payload, assignmentId: rootId })
  })
  return {
    ordered,
    onDragEnd,
  }
}

export const useReorder = (grouped: GroupedItem[]) => {
  const [ordered, setOrdered] = useState<GroupedItem[]>(() => grouped)
  const { updateSubGroupPosition } = useUpdateSubGroupPosition()
  const { updateGroupPosition } = useUpdateGroupPosition()
  const {
    navigationTree: { rootId },
  } = useCurrentTenantData()

  useEffect(() => {
    setOrdered(grouped)
  }, [grouped])

  const groupsSubgroupsRelationsMap = grouped.reduce((acc: any, groupedItem): Group[] => {
    acc[groupedItem.group.id] = groupedItem.subgroups.map(item => item.subgroup)
    return acc
  }, {}) as { [key: string]: Group[] }

  const onDragEnd = useStableCallback((result: DropResult) => {
    const { source, destination, type } = result

    // dropped outside
    if (!destination || source.droppableId !== destination.droppableId) {
      return
    }

    if (type === GROUP_DROPPABLE_TYPE) {
      const groupIds = ordered.map(item => item.group.id)
      const [removed] = groupIds.splice(source.index, 1)
      groupIds.splice(destination.index, 0, removed)

      const orderedGroups = groupIds.map((id, index) => ({
        id,
        order: index + 1,
      }))
      const payload = {
        assignmentId: rootId,
        groups: orderedGroups,
      }

      setOrdered(getOrderedEntities<GroupedItem>('group', ordered, orderedGroups))

      updateGroupPosition(payload)
    } else {
      const subGroupsIds = groupsSubgroupsRelationsMap[source.droppableId].map(({ id }) => id)
      const [removed] = subGroupsIds.splice(source.index, 1)
      subGroupsIds.splice(destination.index, 0, removed)

      const orderedSubGroups = subGroupsIds.map((groupId: string, index: number) => ({
        id: groupId,
        order: index + 1,
        groupId: source.droppableId,
      }))
      const payload = {
        assignmentId: rootId,
        subgroups: orderedSubGroups,
      }

      setOrdered(
        ordered.map(orderedItem => {
          if (orderedItem.group.id === source.droppableId) {
            orderedItem.subgroups = getOrderedEntities<SubGroupItem>(
              'subgroup',
              orderedItem.subgroups,
              orderedSubGroups,
            )
          }
          return orderedItem
        }, []),
      )
      updateSubGroupPosition(payload)
    }
  })

  return {
    ordered,
    onDragEnd,
  }
}

interface GetAppliedTemplateProps {
  pointerNodeId?: string
  appInstances: AppInstanceShort[]
}

export const useGetAppliedTemplateToHierarchy = () => {
  const { data: templates } = useAppInstanceTemplatesApi({
    params: {
      itemsPerPage: PageSize.All,
    },
  })

  const getAppliedTemplate = useCallback(
    ({ pointerNodeId, appInstances }: GetAppliedTemplateProps): TemplateResponse | null => {
      let appliedTemplate: TemplateResponse | null = null
      let appInstancesTemplateId: string | null = null

      if (!templates.length || !appInstances.length || !pointerNodeId) return appliedTemplate

      for (const template of templates) {
        if (template?.appliedOnHierarchies?.some(({ assignmentId }) => assignmentId === pointerNodeId)) {
          appliedTemplate = template
          break
        }
      }

      for (const appInstance of appInstances) {
        if (appInstance.templateId) {
          appInstancesTemplateId = appInstance.templateId
          break
        }
      }

      if (appliedTemplate?.id === appInstancesTemplateId) {
        return appliedTemplate
      }

      return null
    },
    [templates],
  )

  return { getAppliedTemplate }
}
